// All-WebGL streaming pipeline — P4 (STREAM_PIPELINE.md).
//
// Three-native untile: the SAME validated untile fragment shader as WebGlUntile
// (`UNTILE_FS`, parity-gated in P2), but rendered through three into R32F
// WebGLRenderTargets so its outputs are `THREE.Texture`s that the lift pass1 +
// generator dyno sample directly — keeping the whole render side three-managed
// (no foreign-texture adoption, no raw-GL/three state mixing). One RT per plane:
// 0=hGeom 1=hColor 2=bGeom 3=bColor (channel-major c*HW+p, width TW).

import * as THREE from "three";
import { UNTILE_FS, UNTILE_MAXCH, buildUntileLuts } from "../webgl/WebGlUntile";
import type { UntileMeta } from "../webgl/WebGlUntile";
import { FULLSCREEN_VS } from "../webgl/liftGlsl";

const TW = 2048;

export class ThreeUntile {
  readonly width = TW;
  private renderer: THREE.WebGLRenderer;
  private T: number[] = []; private HW: number[] = [];   // per BUFFER: branches may differ in uv_res
  private A: number; private yLo: number; private yRange: number;
  private rts: THREE.WebGLRenderTarget[] = [];
  private maxCh: number[] = [];
  private lut: THREE.Vector4[][] = [];
  private loRng: THREE.Vector2[][] = [];
  private mat: THREE.RawShaderMaterial;
  private scene = new THREE.Scene();
  private cam = new THREE.Camera();
  private atlasTex: THREE.Texture | null = null;

  constructor(renderer: THREE.WebGLRenderer, meta: UntileMeta) {
    this.renderer = renderer;
    this.A = meta.compand_a ?? 0.5; this.yLo = meta.y_lo ?? 0; this.yRange = meta.y_range ?? 255;

    buildUntileLuts(meta).forEach(({ maxCh, lut, loRng, tile }) => {
      this.maxCh.push(maxCh);
      // Per buffer, because a bundle may export its branches at different uv_res
      // (768 head / 256 body). bufSel already splits head from body, so one buffer is
      // one branch and therefore one tile size — the render target sizes with it.
      this.T.push(tile); this.HW.push(tile * tile);
      const h = Math.ceil(maxCh * tile * tile / TW);
      const rt = new THREE.WebGLRenderTarget(TW, h, {
        format: THREE.RedFormat, type: THREE.FloatType,
        minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
        depthBuffer: false, stencilBuffer: false, generateMipmaps: false,
      });
      rt.texture.wrapS = THREE.ClampToEdgeWrapping; rt.texture.wrapT = THREE.ClampToEdgeWrapping;
      this.rts.push(rt);
      const v4: THREE.Vector4[] = [], v2: THREE.Vector2[] = [];
      for (let c = 0; c < UNTILE_MAXCH; c++) {
        v4.push(new THREE.Vector4(lut[c*4], lut[c*4+1], lut[c*4+2], lut[c*4+3]));
        v2.push(new THREE.Vector2(loRng[c*2], loRng[c*2+1]));
      }
      this.lut.push(v4); this.loRng.push(v2);
    });

    this.mat = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: FULLSCREEN_VS.replace("#version 300 es\n", ""),
      fragmentShader: UNTILE_FS.replace("#version 300 es\n", ""),
      depthTest: false, depthWrite: false,
      uniforms: {
        atlas: { value: null }, T: { value: this.T[0] }, HW: { value: this.HW[0] },
        maxCh: { value: 0 }, outW: { value: TW }, lumaMode: { value: 0 },
        A: { value: this.A }, yLo: { value: this.yLo }, yRange: { value: this.yRange },
        lut: { value: this.lut[0] }, loRng: { value: this.loRng[0] },
      },
    });
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(9), 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
    this.scene.add(new THREE.Mesh(geo, this.mat));
  }

  /** Set the decoded atlas as a three texture (e.g. from createImageBitmap, or a
   *  synthetic R8/RGBA8 DataTexture). NEAREST + flipY=false expected. */
  setAtlas(tex: THREE.Texture): void { this.atlasTex = tex; }

  /** Render all four plane RTs. lumaMode "raw" (synthetic byte) | "expanded" (frame). */
  untile(lumaMode: "raw" | "expanded" = "expanded"): void {
    const u = this.mat.uniforms;
    u.atlas.value = this.atlasTex;
    u.lumaMode.value = lumaMode === "raw" ? 1 : 0;
    const prev = this.renderer.getRenderTarget();
    for (let i = 0; i < 4; i++) {
      u.maxCh.value = this.maxCh[i];
      u.T.value = this.T[i];
      u.HW.value = this.HW[i];
      u.lut.value = this.lut[i];
      u.loRng.value = this.loRng[i];
      this.mat.uniformsNeedUpdate = true;
      this.renderer.setRenderTarget(this.rts[i]);
      this.renderer.render(this.scene, this.cam);
    }
    this.renderer.setRenderTarget(prev);
  }

  /** Plane textures: 0=hGeom 1=hColor 2=bGeom 3=bColor. */
  get headGeom(): THREE.Texture { return this.rts[0].texture; }
  get headColor(): THREE.Texture { return this.rts[1].texture; }
  get bodyGeom(): THREE.Texture { return this.rts[2].texture; }
  get bodyColor(): THREE.Texture { return this.rts[3].texture; }

  dispose(): void {
    for (const rt of this.rts) rt.dispose();
    this.scene.traverse((o) => (o as THREE.Mesh).geometry?.dispose?.());
    this.mat.dispose();
  }
}
